import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Loader2, Trash2, Layers, Droplet, ShieldAlert, Ban, Info, Sparkles, ScanLine, Lock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const LOAD_CLS = { low: "text-emerald-600 bg-emerald-50", moderate: "text-amber-600 bg-amber-50", high: "text-red-600 bg-red-50" };
const SEV_CLS = { info: "border-blue-200 bg-blue-50 text-blue-700", caution: "border-amber-200 bg-amber-50 text-amber-800", danger: "border-red-200 bg-red-50 text-red-700" };
const SEV_ICON = { info: Info, caution: ShieldAlert, danger: Ban };

const LoadBox = ({ label, level, t }) => (
  <div className={`flex items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold ${LOAD_CLS[level] || LOAD_CLS.low}`}>
    <span className="flex items-center gap-1.5"><Droplet size={15} /> {label}</span>
    <span className="uppercase">{t(`scan.${level}`) || level}</span>
  </div>
);

const IX = { avoid: "bg-red-50 text-red-700 border-red-200", caution: "bg-amber-50 text-amber-800 border-amber-200" };

const InteractionBadge = ({ ix, t, id }) => {
  if (!ix || !IX[ix.level]) return null;
  return (
    <p data-testid={`stack-interaction-${id}`} className={`mt-2 inline-flex items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${IX[ix.level]}`}>
      <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {t(`ix.${ix.level}`)}: {ix.note}
    </p>
  );
};

const MedsBox = ({ meds, setMeds, save, saving, t }) => (
  <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4" data-testid="stack-meds-box">
    <label className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400"><Droplet size={13} /> {t("ix.myMeds")}</label>
    <div className="flex gap-2">
      <input data-testid="stack-meds-input" value={meds} onChange={(e) => setMeds(e.target.value)} placeholder={t("scan.medsPlaceholder")}
        className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
      <button data-testid="stack-meds-save-button" onClick={save} disabled={saving}
        className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-xl bg-gray-900 px-4 text-sm font-bold text-white hover:bg-gray-800 disabled:opacity-60">
        {saving && <Loader2 size={14} className="animate-spin" />} {t("ix.check")}
      </button>
    </div>
  </div>
);

export default function Stack() {
  const { t, lang } = useLang();
  const { user, setUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meds, setMeds] = useState(user?.medications || "");
  const [savingMeds, setSavingMeds] = useState(false);

  const saveMeds = async () => {
    setSavingMeds(true);
    try {
      const { data } = await api.put("/me/medications", { medications: meds, language: lang });
      setItems(data.items.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
      setUser((u) => ({ ...u, medications: data.medications }));
      toast.success(t("ix.saved"));
    } catch (e) { toast.error(apiError(e)); } finally { setSavingMeds(false); }
  };
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/stack");
      setItems(data.items || []);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/stack/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setResult(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const analyze = async () => {
    setAnalyzing(true); setResult(null);
    try {
      const { data } = await api.post("/stack/analyze", { language: lang, medications: meds });
      setResult(data);
    } catch (e) { toast.error(apiError(e)); } finally { setAnalyzing(false); }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("stack.title")}</h1>
        <p className="mt-1 text-sm text-gray-600">{t("stack.sub")}</p>
      </div>

      {loading ? (
        <div className="flex min-h-[200px] items-center justify-center"><Loader2 className="animate-spin text-emerald-500" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center" data-testid="stack-empty">
          <Layers className="mx-auto mb-3 text-gray-300" size={36} />
          <p className="mb-4 text-sm text-gray-500">{t("stack.empty")}</p>
          <Link to="/scan" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-600">
            <ScanLine size={16} /> {t("stack.goScan")}
          </Link>
        </div>
      ) : (
        <>
          <MedsBox meds={meds} setMeds={setMeds} save={saveMeds} saving={savingMeds} t={t} />
          <div className="space-y-3" data-testid="stack-list">
            {items.map((it) => (
              <div key={it.id} data-testid={`stack-item-${it.id}`} className="flex items-start justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><Layers size={18} /></div>
                  <div>
                    <p className="font-semibold text-gray-800">{it.product_name}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{(it.ingredients || []).map((i) => i.name).filter(Boolean).join(" · ") || "—"}</p>
                    <InteractionBadge ix={it.interaction} t={t} id={it.id} />
                  </div>
                </div>
                <button onClick={() => remove(it.id)} data-testid={`stack-remove-${it.id}`} className="flex shrink-0 items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600">
                  <Trash2 size={13} /> {t("stack.remove")}
                </button>
              </div>
            ))}
          </div>

          {user?.is_pro ? (
            <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
              <button data-testid="stack-analyze-button" onClick={analyze} disabled={analyzing}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60">
                {analyzing ? <><Loader2 size={16} className="animate-spin" /> {t("stack.analyzing")}</> : <><Sparkles size={16} /> {t("stack.analyze")}</>}
              </button>
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="stack-analyze-lock">
              <Lock className="mx-auto mb-2 text-amber-500" size={26} />
              <p className="mb-4 text-sm font-semibold text-amber-800">{t("stack.analyzeLock")}</p>
              <Link to="/pricing" className="inline-block rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-600">{t("pubmed.upgrade")}</Link>
            </div>
          )}

          {result && (
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-4" data-testid="stack-results">
              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <p className="mb-3 text-sm font-bold uppercase tracking-wider text-gray-400">{t("scan.organTitle")}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <LoadBox label={t("stack.combinedKidney")} level={result.combined_kidney} t={t} />
                  <LoadBox label={t("stack.combinedLiver")} level={result.combined_liver} t={t} />
                </div>
                {result.load_note && <p className="mt-3 text-sm leading-relaxed text-gray-600">{result.load_note}</p>}
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-gray-800"><ShieldAlert size={15} className="text-emerald-500" /> {t("stack.interactionsTitle")}</p>
                {result.interactions?.length > 0 ? (
                  <div className="space-y-2">
                    {result.interactions.map((it, i) => {
                      const Ic = SEV_ICON[it.severity] || Info;
                      return (
                        <div key={i} className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${SEV_CLS[it.severity] || SEV_CLS.info}`}>
                          <Ic size={15} className="mt-0.5 shrink-0" />
                          <p><span className="font-bold">{it.with}:</span> {it.note}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-500">{t("stack.noInteractions")}</p>
                )}
              </div>

              {result.summary && (
                <div className="rounded-2xl bg-emerald-50 p-5 text-sm leading-relaxed text-emerald-800">{result.summary}</div>
              )}

              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-800">
                <ShieldAlert size={14} className="mt-0.5 shrink-0" /> {t("scan.safetyNote")}
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
